import React from "react";
import { Reveal, Eyebrow } from "./primitives";
import { SPECIALIZATIONS } from "../../lib/siteData";
import { Aperture, Layers, Columns3, Grid3x3, Brain, Activity, Maximize2, Minimize2, Camera } from "lucide-react";
import { Link } from "react-router-dom";

const SPEC_LINKS = {
  "TLIF": "/specializations/tlif",
  "Open TLIF": "/specializations/open-tlif",
  "MIS TLIF": "/specializations/mis-tlif",
  "Endo TLIF": "/specializations/endo-tlif",
};

const ICONS = {
  aperture: Aperture,
  layers: Layers,
  columns: Columns3,
  grid: Grid3x3,
  brain: Brain,
  activity: Activity,
  maximize: Maximize2,
  minimize: Minimize2,
  camera: Camera,
};

const TRUST = [
  { k: "15+ YEARS", v: "In active practice" },
  { k: "MCh", v: "Neurosurgery" },
  { k: "FELLOWSHIP", v: "Minimally Invasive Spine" },
  { k: "BHUBANESWAR", v: "Odisha, India" },
];

export const TrustStrip = () => (
  <section className="bg-[#1F3864] text-white" data-testid="trust-strip">
    <div className="max-w-[1240px] mx-auto px-5 md:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-4">
      {TRUST.map((t, i) => (
        <Reveal key={t.k} delay={i * 0.08} className="flex flex-col">
          <span className="font-mono-ui text-[#5ECCA0] text-lg md:text-xl tracking-tight">{t.k}</span>
          <span className="text-sm text-white/70 mt-1">{t.v}</span>
        </Reveal>
      ))}
    </div>
  </section>
);

export const Marquee = () => {
  const words = ["Clear explanations", "Honest options", "Minimally invasive", "Patient-first", "Brain & spine"];
  const loop = [...words, ...words];
  return (
    <section className="bg-[#1B3352] py-6 overflow-hidden border-y border-white/5" aria-hidden="true">
      <div className="marquee items-center">
        {loop.map((w, i) => (
          <span key={i} className="flex items-center whitespace-nowrap">
            <span className="font-display italic text-2xl md:text-3xl text-[#F3F5F4]/80 px-8">{w}</span>
            <span className="text-[#5ECCA0] text-xl">◇</span>
          </span>
        ))}
      </div>
    </section>
  );
};

export const Specializations = () => (
  <section id="specializations" className="bg-[#F3F5F4] pt-12 md:pt-16 pb-24 md:pb-32" data-testid="specializations-section">
    <div className="max-w-[1240px] mx-auto px-5 md:px-8">
      <Reveal className="max-w-2xl">
        <Eyebrow>01 — What I treat</Eyebrow>
        <h2 className="font-display font-light text-4xl md:text-5xl text-[#161C22] mt-5 leading-tight">
          Specializations in Bhubaneswar
        </h2>
        <p className="text-[#161C22]/70 mt-4 text-base md:text-lg leading-relaxed">
          A focused range of brain and spine procedures, each explained in plain language
          so you know exactly what a treatment involves.
        </p>
      </Reveal>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-14">
        {SPECIALIZATIONS.map((s, i) => {
          const Icon = ICONS[s.icon] || Activity;
          const link = SPEC_LINKS[s.name];
          const CardTag = link ? Link : "article";
          const cardProps = link ? { to: link } : {};
          return (
            <Reveal key={s.name} delay={(i % 3) * 0.1}>
              <CardTag
                {...cardProps}
                data-testid={`spec-card-${i}`}
                className={`hover-lift h-full block bg-white rounded-2xl border border-[#DCE3E0] p-7 hover:border-[#5ECCA0] hover:shadow-xl hover:shadow-[#1F3864]/15 ${link ? "cursor-pointer" : ""}`}
              >
                <div className="w-12 h-12 rounded-xl bg-[#E3F0EA] flex items-center justify-center text-[#1F3864] mb-6">
                  <Icon size={22} strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-xl text-[#161C22]">{s.name}</h3>
                <p className="text-sm text-[#161C22]/65 mt-3 leading-relaxed">{s.desc}</p>
                {link && <span className="inline-block text-sm text-[#5ECCA0] mt-4 font-medium">Learn more →</span>}
              </CardTag>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);
